import {Banner} from '@primer/react'
import {Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {ThreadPreviewSkeleton} from '../ThreadPreviewSkeleton'
import type {ConversationItem} from '../review-summary-to-issue-comment'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {ThreadPreviewsContent} from './ThreadPreviewsContent'
import {IssueCommentsContent} from './IssueCommentsContent'
import {isReviewCommentsViewMode} from './CommentPanelHeaderContent'
import {useCommentsPanelViewMode, useSelectedCommentReplyThreadId} from '../../../stores/layout-selectors'
import {ViewMode as ViewModeValues} from '../../../stores/slices/comment-view-mode'

export interface CommentsPanelBodyProps {
  filteredIssueCommentIds: Set<string>
  filteredThreadIds: Set<string>
  selectedReviewId: number | null
  handleQuoteReply: (quotedText: string) => void
  isOpen: boolean
  isLazyLoading: boolean
  conversationItems: ConversationItem[]
  limitCount?: number
  limitExceeded?: boolean
  onClose: () => void
  pathName: string
  pullRequestAuthorLogin: string
  threadPreviews: Thread[]
  tabSize?: number
  totalDiffCommentCount: number
}

/**
 * Renders the body content of the Comments Side Panel.
 *
 * Displays either thread previews or issue comments based on the current `viewMode`.
 * When in `thread-previews` mode and the comment limit is exceeded, a warning banner
 * is shown indicating that only the latest comments are visible.
 *
 * Content is lazily loaded via `Suspense` with a skeleton fallback.
 *
 * @param filteredThreadIds - The set of thread IDs to display after filtering has been applied.
 * @param isOpen - Whether the comments panel is currently open.
 * @param limitCount - The maximum number of latest comments shown when the limit is exceeded.
 * @param limitExceeded - Whether the number of comments exceeds the displayable limit.
 * @param onClose - Callback invoked when the panel is closed.
 * @param pathName - The current file path name used to scope displayed comments.
 * @param pullRequestAuthorLogin - The login handle of the pull request author, used for issue comments rendering.
 * @param tabSize - Optional tab size for rendering code snippets.
 * @param threadPreviews - The current set of thread previews to render.
 */
export function CommentsPanelBody({
  filteredIssueCommentIds,
  filteredThreadIds,
  selectedReviewId,
  handleQuoteReply,
  isOpen,
  isLazyLoading,
  conversationItems,
  limitCount,
  limitExceeded,
  onClose,
  pathName,
  pullRequestAuthorLogin,
  tabSize,
  threadPreviews,
  totalDiffCommentCount,
}: CommentsPanelBodyProps) {
  const viewMode = useCommentsPanelViewMode()
  const selectedThreadId = useSelectedCommentReplyThreadId()
  const isReviewCommentsMode = isReviewCommentsViewMode(viewMode)
  const isThreadDetailMode = isReviewCommentsMode && Boolean(selectedThreadId)

  return (
    <>
      {!isThreadDetailMode && isReviewCommentsMode && totalDiffCommentCount > 0 && (
        <span style={{marginTop: '-4px'}} className="mb-2 fgColor-muted text-small text-center d-block">
          Showing {filteredThreadIds.size} of {totalDiffCommentCount} comments
        </span>
      )}
      {!isThreadDetailMode && isReviewCommentsMode && limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
          className="tmp-mb-3"
        />
      )}
      <ErrorBoundary boundaryName="CommentsSidePanelContent" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          {viewMode === ViewModeValues.ThreadPreviews && isLazyLoading ? (
            <ThreadPreviewSkeleton />
          ) : isReviewCommentsMode ? (
            <ThreadPreviewsContent
              filteredThreadIds={filteredThreadIds}
              selectedReviewId={selectedReviewId}
              isOpen={isOpen}
              tabSize={tabSize}
              onClose={onClose}
              threadPreviews={threadPreviews}
            />
          ) : (
            <IssueCommentsContent
              filteredThreadIds={filteredIssueCommentIds}
              isOpen={isOpen}
              conversationItems={conversationItems}
              pathName={pathName}
              pullRequestAuthorLogin={pullRequestAuthorLogin}
              onQuoteReply={handleQuoteReply}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </>
  )
}
